/**
 * ErrorBoundary - Global error boundary component
 * Catches JavaScript errors and provides fallback UI with error reporting
 * Prevents app crashes and provides recovery options
 */

import React, { Component, type ReactNode } from 'react';
import ErrorFallback from '../ui/ErrorFallback';
import { errorMonitoringService } from '../../services/errorMonitoringService';
import { logger } from '../../utils/logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    logger.child('UI').error('ErrorBoundary caught an error:', error, errorInfo);

    // Report error to monitoring service
    errorMonitoringService.captureReactError(error, {
      componentStack: errorInfo?.componentStack ?? '',
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError && this.state.error) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Use enhanced error fallback
      return (
        <ErrorFallback
          error={this.state.error}
          resetErrorBoundary={this.handleRetry}
          componentStack={this.state.errorInfo?.componentStack ?? undefined}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;